import React, { useContext } from "react"
import { StyleSheet, View } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { SkeletonBlock, SkeletonCircle, SkeletonGroup, SkeletonLine } from "../utility/skeleton"

export const SpendingImpactSkeleton = ({
  recentTransactionsCount = 3,
}: {
  recentTransactionsCount?: number
}): React.ReactElement => {
  const theme = useContext(ThemeContext).theme
  const { bgMain } = theme.colors

  const txCount = Math.max(2, Math.min(5, recentTransactionsCount))

  return (
    <View style={{ backgroundColor: bgMain }}>
      <SkeletonGroup>
        {/* Intro blurb line */}
        <View style={{ gap: 8 }}>
          <SkeletonLine width="95%" height={12} radius={8} />
          <SkeletonLine width="75%" height={12} radius={8} />
        </View>

        {/* Spending Breakdown */}
        <View style={{ gap: 10, marginTop: 14 }}>
          <SkeletonLine width="45%" height={18} radius={10} />
          <SkeletonLine width="70%" height={12} radius={8} />

          {/* Summary tiles row */}
          <View style={styles.tilesRow}>
            <SkeletonBlock style={styles.tile} radius={12} />
            <SkeletonBlock style={styles.tile} radius={12} />
          </View>

          {/* Values breakdown table */}
          <View style={styles.tableBlock}>
            <View style={styles.tableHeader}>
              <SkeletonLine width="22%" height={11} radius={6} />
              <SkeletonLine width="8%" height={11} radius={6} />
              <SkeletonLine width="14%" height={11} radius={6} />
            </View>
            {new Array(6).fill(0).map((_, idx) => (
              <View key={idx} style={styles.tableRow}>
                <View style={styles.tableRowLeft}>
                  <SkeletonCircle size={28} />
                  <SkeletonLine width="62%" height={13} radius={8} />
                </View>
                <SkeletonLine width={36} height={13} radius={6} />
                <SkeletonLine width={52} height={13} radius={6} />
              </View>
            ))}
          </View>
        </View>

        {/* Recent transactions */}
        <View style={{ gap: 10, marginTop: 18 }}>
          <SkeletonLine width="70%" height={18} radius={10} />
          {new Array(txCount).fill(0).map((_, idx) => (
            <View key={idx} style={styles.txRow}>
              <SkeletonBlock style={styles.txColorBand} radius={0} />
              <SkeletonBlock style={styles.txIcon} radius={8} />
              <View style={{ flex: 1, gap: 8 }}>
                <SkeletonLine width="70%" height={14} radius={8} />
                <SkeletonLine width="55%" height={12} radius={8} />
              </View>
              <View style={{ width: 90, alignItems: "flex-end", gap: 8 }}>
                <SkeletonLine width={70} height={14} radius={8} />
                <SkeletonLine width={55} height={10} radius={8} />
              </View>
            </View>
          ))}
        </View>
      </SkeletonGroup>
    </View>
  )
}

const styles = StyleSheet.create({
  tilesRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    columnGap: 10,
    rowGap: 10,
  },
  tile: {
    flex: 1,
    minWidth: 150,
    height: 74,
  },
  tableBlock: {
    width: "100%",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 10,
    marginTop: 6,
  },
  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 6,
    gap: 12,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 6,
  },
  tableRowLeft: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  txRow: {
    width: "100%",
    borderRadius: 10,
    overflow: "hidden",
    flexDirection: "row",
    gap: 12,
    padding: 12,
    alignItems: "center",
  },
  txColorBand: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: 10,
  },
  txIcon: {
    width: 50,
    height: 50,
  },
})

